// src/components/FeaturedCategories.jsx
import { ArrowRight } from 'lucide-react';
import { Link } from "react-router";
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { getPublicVehicles } from '../../services/publicVehicle.service';
import { useCms } from '../hooks/useCms';

type Category = {
  title: string;
  value: string;
  image: string;
  link: string;
  key: string; // Added for translation
};

const categories: Category[] = [
  {
    title: 'Cars',
    value: 'Car',
    link: "/search?category=Car",
    key: 'cars',
    image: 'https://images.unsplash.com/photo-1485291571150-772bcfc10da5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcG9ydHMlMjBjYXJ8ZW58MXx8fHwxNzgwOTA0NjM1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
  },
  {
    title: 'Motorcycles',
    value: 'Motorcycle',
    link: "/search?category=Motorcycle",
    key: 'motorcycles',
    image: 'https://images.unsplash.com/photo-1609630875171-b1321377ee65?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtb3RvcmN5Y2xlfGVufDF8fHx8MTc4MDkxNDUwM3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
  },
  {
    title: 'ATV',
    value: 'ATV',
    link: "/search?category=ATV",
    key: 'atv',
    image: 'https://images.unsplash.com/photo-1496521061024-90e1c1221555?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxBVFYlMjBxdWFkJTIwYmlrZXxlbnwxfHx8fDE3ODA5MTQ1MDR8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
  },
  {
    title: 'Automotive Parts',
    value: 'Parts',
    link: "/search?category=Parts",
    key: 'parts',
    image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  },
];

export function FeaturedCategories() {
  const { t } = useTranslation('home');
  const { get } = useCms('home');
  const [listingCounts, setListingCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;

    const loadListingCounts = async () => {
      try {
        // Only one record is requested for each category; `total_records` is the
        // database count after the same public-search filters used by /search.
        const responses = await Promise.all(
          categories.map((category) =>
            getPublicVehicles({ page: 1, pageSize: 1, category: category.value }),
          ),
        );

        if (cancelled) return;

        setListingCounts(
          Object.fromEntries(
            categories.map((category, index) => [
              category.value,
              responses[index]?.pagination?.total_records ?? 0,
            ]),
          ),
        );
      } catch (error) {
        // Leave the count unavailable rather than showing a misleading static value.
        console.error('Unable to load category listing counts', error);
      }
    };

    void loadListingCounts();
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="py-16 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl text-[#2D323A] mb-4">
            {get('categories.categoryTitle', t('categories.title'))}
          </h2>
          <p className="text-lg text-[#717182]">
            {get('categories.categoryDescription', t('categories.subtitle'))}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((category, index) => {
            const displayName = get(`categories.category${index + 1}Name`, category.title);
            return (
            <Link
              to={category.link}
              key={category.title}
              className="group relative overflow-hidden rounded-2xl bg-white shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={category.image}
                  alt={displayName}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-2xl mb-1">{displayName}</h3>
                <p className="text-[#BFC2C7] mb-4">
                  {listingCounts[category.value]?.toLocaleString() ?? '—'} Listings
                </p>
                <button className="inline-flex items-center gap-2 text-white group-hover:gap-3 transition-all">
                  <span>{t('categories.browse')}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Link>
          );})}
        </div>
      </div>
    </section>
  );
}